/**
 * A sessão do app unificado como máquina de estados.
 *
 *   loading → anonymous → (login) → choosing → (assume) → ready
 *                                 ↘ direto quando há um vínculo ou lembrança
 *
 * A casca decide o que renderizar a partir de `status`; os portais só existem
 * em `ready`, e `context` é garantido lá. Ver A003 (sempre switch-context) e
 * A006 (vínculo lembrado por conta).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { onSessionExpired, refreshAccessToken } from '../api/client';
import { decideEntry, type EntryDecision } from './decide';
import { sessionApi } from './session.api';
import { sessionStore } from './store';
import type { AvailableContext, SessionContext, SessionUser } from './types';
import type { BrandRole } from '@eva/schemas';

export type SessionStatus = 'loading' | 'anonymous' | 'choosing' | 'ready';

export interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  context: SessionContext | null;
  contexts: AvailableContext[];
}

export interface SessionActions {
  /** Login; devolve a decisão de entrada para a casca navegar. */
  login: (email: string, password: string, partnerSlug?: string | null) => Promise<EntryDecision>;
  /** Assume um vínculo (troca o token). Lembra a escolha para esta conta. */
  assume: (ctx: AvailableContext, opts?: { remember?: boolean }) => Promise<SessionContext>;
  /** Volta à escolha sem sair: esquece a lembrança e recarrega os vínculos. */
  reopenChooser: () => Promise<void>;
  reloadContexts: () => Promise<AvailableContext[]>;
  logout: () => Promise<void>;
  /** Atualiza o usuário guardado (ex.: depois de editar o perfil). */
  setUser: (u: SessionUser) => void;
}

const Ctx = createContext<(SessionState & SessionActions) | null>(null);

// Access token dura 15 min na API; renovar aos 14 evita que a primeira
// chamada depois de uma pausa leve um 401 + refresh + repetição.
const REFRESH_PROATIVO_MS = 14 * 60 * 1000;

export interface SessionProviderProps {
  children: ReactNode;
  /**
   * Chamado com o papel do contexto ANTES de a sessão virar `ready`.
   *
   * A casca usa para baixar o chunk do portal enquanto o botão de entrar
   * ainda gira. Sem isto, `ready` chega, a tela de login sai, e o que aparece
   * até o chunk descer é uma tela vazia com um spinner — no portal da marca,
   * que é o painel inteiro (2,8 MB minificado), isso é uma piscada visível
   * em QA. Falha aqui não impede a entrada: o `lazy()` da rota tenta de novo.
   */
  prepararPortal?: (role: BrandRole) => Promise<unknown>;
}

export function SessionProvider({ children, prepararPortal }: SessionProviderProps) {
  const [state, setState] = useState<SessionState>({ status: 'loading', user: null, context: null, contexts: [] });
  const timer = useRef<number | null>(null);

  const limpar = useCallback(() => {
    sessionStore.clear();
    setState({ status: 'anonymous', user: null, context: null, contexts: [] });
  }, []);

  /**
   * Troca o token pelo contexto e grava no storage — sem publicar estado.
   *
   * Separado de `assume` porque a entrada direta (um vínculo só, ou lembrado)
   * precisa ir de `anonymous` a `ready` num passo. A versão anterior publicava
   * `choosing` antes de trocar o token: a tela de login redirecionava para
   * `/contextos`, a escolha aparecia com um cartão só e sumia quando o token
   * chegava. Em localhost eram milissegundos; em QA, uma piscada visível.
   */
  const trocarContexto = useCallback(async (ctx: AvailableContext, opts?: { remember?: boolean }) => {
    const r = await sessionApi.switchContext(ctx);
    sessionStore.writeTokens({ accessToken: r.accessToken, refreshToken: r.refreshToken });
    const contexto: SessionContext = { ...r.context, brandName: ctx.brandName ?? r.context.brandName };
    sessionStore.writeContext(contexto);
    const user = sessionStore.readUser();
    if (user && opts?.remember !== false) sessionStore.remember(user.id, contexto);
    // Só depois do token: preparar um portal que a API acabou de recusar
    // seria trabalho jogado fora.
    if (prepararPortal) await prepararPortal(contexto.role).catch(() => undefined);
    return contexto;
  }, [prepararPortal]);

  const assume = useCallback<SessionActions['assume']>(async (ctx, opts) => {
    const contexto = await trocarContexto(ctx, opts);
    setState((s) => ({ ...s, status: 'ready', context: contexto }));
    return contexto;
  }, [trocarContexto]);

  /**
   * Aplica a regra de entrada a uma lista de vínculos e publica o estado
   * final de uma vez: `ready` quando a decisão é direta, `choosing` quando
   * há o que escolher. Nunca passa por `choosing` a caminho de `ready`.
   */
  const entrarComVinculos = useCallback(
    async (user: SessionUser, contexts: AvailableContext[]): Promise<EntryDecision> => {
      const decisao = decideEntry(contexts, sessionStore.rememberedFor(user.id));
      if (decisao.kind === 'direct') {
        const contexto = await trocarContexto(decisao.context, { remember: decisao.reason === 'remembered' });
        setState({ status: 'ready', user, context: contexto, contexts });
      } else {
        setState({ status: 'choosing', user, context: null, contexts });
      }
      return decisao;
    },
    [trocarContexto]
  );

  // Boot: reconstrói do storage. Token + contexto → pronto; token sem
  // contexto (sessão interrompida no meio da escolha) → escolher de novo.
  useEffect(() => {
    const { accessToken, refreshToken } = sessionStore.readTokens();
    const user = sessionStore.readUser();
    const context = sessionStore.readContext();

    if (!accessToken || !refreshToken || !user) {
      limpar();
      return;
    }

    if (context) {
      setState({ status: 'ready', user, context, contexts: [] });
      // Confirma em segundo plano que a sessão ainda vale e atualiza o usuário.
      sessionApi
        .me()
        .then(({ user: atual }) => {
          sessionStore.writeUser(atual);
          setState((s) => (s.status === 'ready' ? { ...s, user: atual } : s));
        })
        .catch(() => {
          /* 401 é tratado pelo cliente via onSessionExpired */
        });
      return;
    }

    // Mesma regra de entrada do login: um vínculo entra direto, sem parar na
    // escolha. Antes o boot sempre publicava `choosing`, e quem tinha um
    // vínculo só via a tela de escolha com um cartão para clicar.
    sessionApi
      .listContexts()
      .then(({ contexts }) => entrarComVinculos(user, contexts))
      .catch(() => limpar());
  }, [limpar, entrarComVinculos]);

  // Sessão morreu no cliente HTTP (refresh falhou): volta ao início.
  useEffect(() => onSessionExpired(limpar), [limpar]);

  // Refresh proativo enquanto houver sessão.
  useEffect(() => {
    if (timer.current) window.clearInterval(timer.current);
    if (state.status !== 'ready') return;
    timer.current = window.setInterval(() => {
      void refreshAccessToken();
    }, REFRESH_PROATIVO_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [state.status]);

  const login = useCallback<SessionActions['login']>(
    async (email, password, partnerSlug) => {
      const r = await sessionApi.login(email, password, partnerSlug);
      sessionStore.writeTokens({ accessToken: r.accessToken, refreshToken: r.refreshToken });
      sessionStore.writeUser(r.user);

      const { contexts } = await sessionApi.listContexts();
      return entrarComVinculos(r.user, contexts);
    },
    [entrarComVinculos]
  );

  const reloadContexts = useCallback(async () => {
    const { contexts } = await sessionApi.listContexts();
    setState((s) => ({ ...s, contexts }));
    return contexts;
  }, []);

  const reopenChooser = useCallback(async () => {
    const user = sessionStore.readUser();
    if (user) sessionStore.forget(user.id);
    const contexts = await reloadContexts();
    setState((s) => ({ ...s, status: 'choosing', contexts }));
  }, [reloadContexts]);

  const logout = useCallback(async () => {
    const { refreshToken } = sessionStore.readTokens();
    const conta = sessionStore.readUser();
    await sessionApi.logout(refreshToken);
    // O onboarding guardado desta conta (A086) sai com ela — no "Sair", e só
    // nele: a sessão vencida também limpa a sessão, e apagar ali desfaria o
    // que o progresso guardado existe para salvar.
    try {
      if (conta?.id) localStorage.removeItem(`eva_onboarding:${conta.id}`);
    } catch {
      /* sem storage, nada a apagar */
    }
    limpar();
  }, [limpar]);

  const setUser = useCallback((u: SessionUser) => {
    sessionStore.writeUser(u);
    setState((s) => ({ ...s, user: u }));
  }, []);

  const value = useMemo(
    () => ({ ...state, login, assume, reopenChooser, reloadContexts, logout, setUser }),
    [state, login, assume, reopenChooser, reloadContexts, logout, setUser]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState & SessionActions {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession precisa de <SessionProvider>');
  return v;
}

/** Só para portais: garante `user` e `context` não nulos. */
export type ReadySession = Omit<SessionState, 'user' | 'context'> & { user: SessionUser; context: SessionContext } & SessionActions;

export function useReadySession(): ReadySession {
  const s = useSession();
  if (s.status !== 'ready' || !s.user || !s.context) {
    throw new Error('useReadySession fora de uma sessão pronta');
  }
  return s as ReadySession;
}
